import { type ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "../../lib/utils";

type SwitchProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "onChange" | "type" | "role" | "aria-checked"
> & {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label?: string;
};

const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { checked, onCheckedChange, label, disabled, className, ...props },
  ref
) {
  const trackClass = checked ? "bg-ds-primary" : "bg-ds-secondary";
  // Thumb inverts against the track so it reads in both states: light
  // (secondary) knob on the dark primary track, dark (primary) knob on the
  // light secondary track.
  const thumbColor = checked ? "bg-ds-secondary" : "bg-ds-primary";
  return (
    <button
      ref={ref}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        onCheckedChange(!checked);
      }}
      className={cn(
        "relative inline-flex h-6 w-10 shrink-0 items-center rounded-full p-0.5",
        "transition-colors duration-150 ease-out",
        "cursor-pointer select-none",
        "focus-visible:outline-none focus-visible:shadow-focus-ring",
        "disabled:cursor-not-allowed disabled:opacity-50",
        trackClass,
        className
      )}
      {...props}
    >
      <span
        className={cn(
          "block size-5 rounded-full shadow-card-subtle",
          "transition-[transform,background-color] duration-150 ease-out",
          checked ? "translate-x-4" : "translate-x-0",
          thumbColor
        )}
      />
    </button>
  );
});

export { Switch, type SwitchProps };
